import { useResetUserPassword } from '@Hooks/index';
import {
    Button,
    ButtonWithConfirmationModal,
    DivWithSpacing,
    EnumMessageLevel,
    EnumMessageType,
    FlexColumn,
    Modal,
} from '@synqo/frontend-component-library';
import { useCallback, useState } from 'react';

interface ResetPasswordButtonProps {
    userId: string;
    refetchData: () => void;
    /** Whether a password reset may be started at all. */
    enabled: boolean;
    /** Explains to the user why the button is disabled, when there is something worth saying. */
    disabledTooltip?: string;
}

export function ResetPasswordButton({ userId, refetchData, enabled, disabledTooltip }: ResetPasswordButtonProps) {
    const [isResetPasswordInfoModalOpen, setIsResetPasswordInfoModalOpen] = useState(false);
    const { resetPasswordHandler, resetUserPasswordIsPending } = useResetUserPassword(userId);

    const resetPassword = useCallback(async () => {
        if (await resetPasswordHandler()) {
            setIsResetPasswordInfoModalOpen(true);
        }
    }, [resetPasswordHandler]);

    const onResetPasswordInfoModalClose = useCallback(() => {
        setIsResetPasswordInfoModalOpen(false);
        refetchData();
    }, [refetchData]);

    return (
        <>
            <ButtonWithConfirmationModal
                key="password_reset_button"
                onClick={resetPassword}
                locked={!enabled || resetUserPasswordIsPending}
                tooltip={disabledTooltip ?? null}
                confirmModalMessage={{
                    message: 'Wollen Sie das Passwort wirklich zurücksetzen?',
                    title: 'Zurücksetzen bestätigen',
                    type: EnumMessageType.MultipleChoice,
                    level: EnumMessageLevel.Information,
                }}
                variant="secondary"
            >
                {resetUserPasswordIsPending ? 'Passwort wird zurückgesetzt' : 'Passwort zurücksetzen'}
            </ButtonWithConfirmationModal>
            <Modal
                isOpen={isResetPasswordInfoModalOpen}
                onClose={onResetPasswordInfoModalClose}
                title={'Passwort wird zurückgesetzt'}
                footer={
                    <DivWithSpacing margin={{ top: 'md' }}>
                        <FlexColumn align="stretch">
                            <Button onClick={onResetPasswordInfoModalClose} variant="primary">
                                Ok
                            </Button>
                        </FlexColumn>
                    </DivWithSpacing>
                }
                withoutCloseButton
                bodyProps={{ type: 'never' }}
            >
                <div className={'grid max-w-md gap-4'}>
                    <p>
                        Die Passwortzurücksetzung wurde erfolgreich durchgeführt. Es kann einige Minuten dauern, bis die
                        Änderung wirksam ist.
                    </p>
                </div>
            </Modal>
        </>
    );
}
